import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    BatteryHigh,
    CaretRight,
    Check,
    CheckCircle,
    Circle,
    CircleNotch,
    CurrencyDollar,
    House,
    IdentificationBadge,
    Info,
    MapPin,
    Package,
    Plus,
    RocketLaunch,
    Sparkle,
    Sun,
    TreeStructure,
    WarningCircle,
    Wrench,
    X,
} from '@phosphor-icons/react';
import { DEFAULT_SITE_VIEWER_MESSAGE } from '../../helpers/siteViewer3dAccess';
import { persistDashboardLayoutVariant } from '../../helpers/dashboardLayoutVariant';
import { AlertType } from '../../@types/enums';
import { notification } from 'antd';
import { useOneButtonStore } from '../../store/oneButtonStore';
import AddTitleContactsModal from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/AddTitleContactsModal';
import GenerateQuoteModal, {
    type QuoteFormState,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/GenerateQuoteModal';
import {
    type selectedQuote,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/FinancingGridCard';
import { buildSelectedQuoteFromOpportunityProductTypes } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/buildGenerateQuoteFromFinanceLine';
import { getOneButtonCapabilityValue } from '../../services/oneButtonServices/capabilityService';
import quoteServices from '../../services/oneButtonServices/quoteServices';
import { RoofDetailsModal } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/RoofDetailsModal';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/GenerateQuoteModal.css';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/ProductDesignsModal.css';
import designServices from '../../services/oneButtonServices/designServices';
import { useViewerStore } from '../../store/store';
import {
    clearDisabledPreContractProducts,
    defaultIntakeForm,
    loadIntakeForm,
    persistIntakeForm,
    PRE_CONTRACT_DISABLED_PRODUCTS,
    PRE_CONTRACT_PRODUCTS,
    type PreContractDesignTool,
    type PreContractIntakeForm,
    type PreContractProduct,
    type PreContractRoofModuleLayout,
    type PreContractSiteContext,
    US_STATE_OPTIONS,
} from '../lib/preContractTypes';
import { hydrateOpportunityStore, resolveStoreOpportunityId } from '../lib/preContractTitle';
import {
    fetchUtilityPrefillFromOpportunity,
    hasUtilityPrefill,
    mergeUtilityPrefill,
    utilityCompanySelectOptions,
} from '../lib/preContractUtilityPrefill';
import {
    ensureSystemDesignForPrefill,
    fetchAndStoreEnergyCommunityStatus,
    fetchOpportunityPrefillContext,
    hasProductPrefill,
    mergeBatteryPrefillFromSystemDesign,
    mergeProductPrefill,
} from '../lib/preContractOpportunityPrefill';
import {
    canLaunchWorkspace,
    canRunAiDesigns,
    canAdvanceFromIntakeBoard,
    CONTENT_STEPS,
    designAvailabilityIssues,
    formatInstallAddress,
    getStepValidation,
    hasActiveDesignTool,
    type StepValidation,
    type WizardStepId,
} from '../lib/preContractValidation';
import {
    PreContractBatterySection,
    PreContractRoofSlopeSection,
} from './PreContractDesignExtras';
import { PreContractOneDrawEmbed } from './PreContractOneDrawEmbed';
import {
    applyRoofSelectionsToLayouts,
    collectIncludedModuleIds,
    mergeLiveModuleLayouts,
    mergePciSiteTrees,
    pciGhostedTreeIdKey,
    pciRoofModuleLayoutsEqual,
    pciSiteRoofsEqual,
    pciSiteTreesLookEqual,
    PCI_ONEDRAW_LOAD_ERROR_MSG,
    PCI_ONEDRAW_ROOFS_MSG,
    postPci3dViewToEmbed,
    postPciApplyDesignOptionToEmbed,
    postPciModuleSelectionToEmbed,
    postPciRoofSelectionToEmbed,
    postPciTreeGhostToEmbed,
    postPciViewportResizeToEmbed,
    requestPciModulesLayoutFromEmbed,
    sumPciSelectedRoofSquares,
    type PciSiteRoof,
    type PciSiteTree,
} from '../lib/preContractEmbedBridge';
import { PreContractModuleStep } from './PreContractModuleStep';
import { PreContractAiDesignsStep } from './PreContractAiDesignsStep';
import { PreContractSelectedDesignStep } from './PreContractSelectedDesignStep';
import {
    intakePatchFromAiDesign,
    type PreContractAiDesignOption,
    clearPreContractAiOptionsCache,
    hasPreContractAiOptionsCache,
    resolveSelectedPreContractAiOption,
} from '../lib/preContractAiDesigns';
import { PreContractLivePricingRail } from './PreContractLivePricingRail';
import { PreContractSelectedPricingPreview } from './PreContractSelectedPricingPreview';
import { resolveEnergyCommunityLikelyQualifies } from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/EnergyCommunityNotice';
import {
    buildPreContractRoofDesignSnapshot,
    patchFormFromRoofDesignSnapshot,
} from '../lib/preContractRoofDesign';
import {
    isSolarLayoutModifiedFromBaseline,
    solarLayoutSignature,
} from '../lib/preContractSolarDesign';
import { PreContractProductDealCard } from './PreContractProductDealCard';
import {
    preContractSystemSizeKw,
} from '../lib/preContractPricing';
import {
    buildRoofModuleLayouts,
    countIncludedModules,
    estimateLayoutAnnualKwh,
    estimateTreeCount,
    maxFillRoofSelections,
} from '../lib/preContractModuleLayout';
import {
    markPreContractAutoFinalize,
    markPreContractLaunch,
    markPreContractReturn,
    persistPreContractOpenDesign,
    peekPreContractReturnStep,
    type PreContractLaunchAction,
} from '../lib/preContractLaunch';
import type { PreContractPpwMetricsOverride } from '../lib/preContractPpwPricing';
import { savePreContractOneButtonSelection } from '../lib/preContractSaveOneButton';
import {
    extractTitleFields,
    isTitleInfoAvailable,
    loadPreContractTitleContext,
} from '../lib/preContractTitle';
import {
    opportunityHasSyncedTitleContact,
    syncedTitleContactName,
} from '../../helpers/titleContactStatus';

const PRODUCT_META: Record<
    PreContractProduct,
    { description: string; icon: React.ReactNode; accent: string }
> = {
    Solar: {
        description: 'Photovoltaic system & production modeling',
        icon: <Sun size={18} weight="duotone" />,
        accent: 'amber',
    },
    Roof: {
        description: 'Full or partial roof replacement',
        icon: <House size={18} weight="duotone" />,
        accent: 'slate',
    },
    Battery: {
        description: 'Energy storage & backup capacity',
        icon: <BatteryHigh size={18} weight="duotone" />,
        accent: 'green',
    },
    RR: {
        description: 'Remove & reinstall existing arrays',
        icon: <Wrench size={18} weight="duotone" />,
        accent: 'blue',
    },
    RRR: {
        description: 'Remove, roof, and reinstall workflow',
        icon: <TreeStructure size={18} weight="duotone" />,
        accent: 'violet',
    },
};

export type PreContractIntakeProps = {
    site: PreContractSiteContext;
    userName?: string;
};

type PanelId = 'site' | 'design' | 'title';

const PANEL_STEPS: Record<PanelId, WizardStepId> = {
    site: 'site',
    design: 'design',
    title: 'property',
};

const PANEL_LABELS: Record<PanelId, string> = {
    site: 'Site address',
    design: 'Design',
    title: 'Title',
};

function productPillLabel(product: PreContractProduct): string {
    if (product === 'RRR') return 'R&R';
    return product.toUpperCase();
}

function stepToPanel(stepId: WizardStepId): PanelId | 'utility' | 'designTool' | null {
    switch (stepId) {
        case 'site':
            return 'site';
        case 'property':
            return 'title';
        case 'design':
            return 'design';
        case 'utility':
            return 'utility';
        default:
            return null;
    }
}

const TILE_STEPS: WizardStepId[] = ['products', 'site', 'property', 'utility', 'design'];

const UTILITY_POPOVER_WIDTH = 320;
const UTILITY_POPOVER_GAP = 10;
const VIEWPORT_MARGIN = 8;

type DesignViewerDockRect = {
    left: number;
    top: number;
    width: number;
    height: number;
    /** Pixels of the docked iframe that sit under the unified header while scrolling. */
    clipTop: number;
};

/** Unified header height; the docked 3D viewport must stay visually below this band. */
function getAppUnifiedHeaderHeight(): number {
    const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--app-unified-header-height')
        .trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60;
}

function applyPciDockHostStyles(
    host: HTMLElement,
    next: DesignViewerDockRect,
    mode: 'fixed' | 'in-slot' = 'fixed',
) {
    if (mode === 'in-slot') {
        // Host is portaled into the stack slot — fill it in document flow.
        host.style.left = '';
        host.style.top = '';
        host.style.width = '100%';
        host.style.height = '100%';
        host.style.clipPath = 'none';
        return;
    }
    host.style.left = `${next.left}px`;
    host.style.top = `${next.top}px`;
    host.style.width = `${next.width}px`;
    host.style.height = `${next.height}px`;
    host.style.clipPath = next.clipTop > 0 ? `inset(${next.clipTop}px 0 0 0)` : 'none';
}

/** Portal into `.app` so docked z-index stacks under `.app-unified-header`. */
function getPciViewportPortalRoot(): HTMLElement {
    return document.querySelector('.app') ?? document.body;
}

export const PreContractIntake: React.FC<PreContractIntakeProps> = ({ site }) => {
    const navigate = useNavigate();
    const utilityButtonRef = useRef<HTMLButtonElement>(null);
    const utilityWrapRef = useRef<HTMLDivElement>(null);
    const utilityPopoverRef = useRef<HTMLDivElement>(null);
    const designToolButtonRef = useRef<HTMLButtonElement>(null);
    const designToolPopoverRef = useRef<HTMLDivElement>(null);
    const designViewerHostRef = useRef<HTMLDivElement>(null);
    const designViewerSlotRef = useRef<HTMLDivElement>(null);
    /** Keep park size = last dock size so WebGL isn't resized cold on every open. */
    const lastDesignViewerDockRectRef = useRef<DesignViewerDockRect | null>(null);
    const [designViewerDocked, setDesignViewerDocked] = useState(false);
    /** Live slot node so the host can portal into the stack instead of floating on `.app`. */
    const [dockSlotEl, setDockSlotEl] = useState<HTMLElement | null>(null);

    const [form, setForm] = useState<PreContractIntakeForm>(() => {
        const saved = loadIntakeForm(site.salesForceId);
        const base = saved ?? defaultIntakeForm(site);
        return {
            ...base,
            products: clearDisabledPreContractProducts(base.products),
        };
    });
    const hadSavedIntakeFormRef = useRef(Boolean(loadIntakeForm(site.salesForceId)));
    const savedSolarBaselineRef = useRef<string | null>(
        (() => {
            const saved = loadIntakeForm(site.salesForceId);
            return saved ? solarLayoutSignature(saved) : null;
        })(),
    );

    const [activePanel, setActivePanel] = useState<PanelId | null>(null);
    const [utilityPopoverOpen, setUtilityPopoverOpen] = useState(false);
    const [utilityPopoverPos, setUtilityPopoverPos] = useState<{
        top: number;
        left: number;
        placement: 'below' | 'above';
    } | null>(null);
    const [designToolPopoverOpen, setDesignToolPopoverOpen] = useState(false);
    const [designToolPopoverPos, setDesignToolPopoverPos] = useState<{
        top: number;
        left: number;
        placement: 'below' | 'above';
    } | null>(null);
    const [highlightValidation, setHighlightValidation] = useState(false);
    const [utilityFromOpportunity, setUtilityFromOpportunity] = useState(false);
    const [boardStep, setBoardStep] = useState<1 | 2 | 3 | 4>(() => peekPreContractReturnStep() ?? 1);
    const [aiDesignRunId, setAiDesignRunId] = useState(0);
    const preAiSnapshotRef = useRef<{
        roofModuleLayouts: PreContractIntakeForm['roofModuleLayouts'];
        roofSelections: boolean[];
        systemUsageKwh: string;
    } | null>(null);
    const [pricingPanelOpen, setPricingPanelOpen] = useState(false);
    const [treeCuttingPreviewActive, setTreeCuttingPreviewActive] = useState(false);
    const [viewportPopupOpen, setViewportPopupOpen] = useState(false);
    const [solarLayoutPopupOpen, setSolarLayoutPopupOpen] = useState(false);
    const [aiDesignsPopupOpen, setAiDesignsPopupOpen] = useState(false);
    const [productPickerOpen, setProductPickerOpen] = useState(false);
    const [designProductFocus, setDesignProductFocus] = useState<PreContractProduct | null>(null);
    const [designDrawerFooter, setDesignDrawerFooter] = useState<ReactNode | null>(null);
    const [confirmQuoteCellKey, setConfirmQuoteCellKey] = useState<selectedQuote | null>(null);
    const [quoteForm, setQuoteForm] = useState<QuoteFormState | null>(null);
    const [generateQuoteLoading, setGenerateQuoteLoading] = useState(false);
    /** Keep oneDRAW iframe warm from page load so roof data posts before Design opens. */
    const [designViewerKept, setDesignViewerKept] = useState(() => Boolean(site.salesForceId?.trim()));
    /** Actual roofs from the embedded oneDRAW site model. */
    const [siteRoofs, setSiteRoofs] = useState<PciSiteRoof[]>([]);
    const [siteViewportError, setSiteViewportError] = useState<string | null>(null);
    const lastPostedGhostKeyRef = useRef<string>('');
    const [titleLoading, setTitleLoading] = useState(true);
    const [titleInfo, setTitleInfo] = useState<Record<string, unknown> | null>(null);
    const [titleSearchData, setTitleSearchData] = useState<unknown[]>([]);
    const [addTitleContactsModalOpen, setAddTitleContactsModalOpen] = useState(false);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const energyCommunityStatus = useOneButtonStore((s) => s.energyCommunityStatus);
    const priceQuoteDetails = useOneButtonStore((s) => s.priceQuoteDetails);
    const setPriceQuoteDetails = useOneButtonStore((s) => s.setPriceQuoteDetails);
    const setQuotesRefreshTrigger = useOneButtonStore((s) => s.setQuotesRefreshTrigger);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const setSystemDesign = useOneButtonStore((s) => s.setSystemDesign);
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const theme = useOneButtonStore((s) => s.theme);
    const storeSalesForceId = useOneButtonStore((s) => s.salesForceId);
    const titleContactSynced =
        Boolean(site.salesForceId) &&
        String(storeSalesForceId ?? '') === String(site.salesForceId) &&
        opportunityHasSyncedTitleContact(opportunityDetails);
    const titleContactName = titleContactSynced ? syncedTitleContactName(opportunityDetails) : '';

    const refreshTitleContext = useCallback(async (options?: { silent?: boolean }) => {
        if (!options?.silent) setTitleLoading(true);
        try {
            const next = await loadPreContractTitleContext(site.salesForceId);
            await fetchAndStoreEnergyCommunityStatus(site.salesForceId);
            setTitleInfo(next.titleInfo);
            setTitleSearchData(next.titleSearchData);
        } finally {
            setTitleLoading(false);
        }
    }, [site.salesForceId]);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            setTitleLoading(true);
            const next = await loadPreContractTitleContext(site.salesForceId);
            await fetchAndStoreEnergyCommunityStatus(site.salesForceId);
            if (cancelled) return;
            setTitleInfo(next.titleInfo);
            setTitleSearchData(next.titleSearchData);
            setTitleLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [site.salesForceId]);

    useEffect(() => {
        let cancelled = false;

        void (async () => {
            const [utilityPrefill, opportunityContext] = await Promise.all([
                fetchUtilityPrefillFromOpportunity(site.salesForceId),
                fetchOpportunityPrefillContext(site.salesForceId),
            ]);
            if (cancelled) return;

            setForm((prev) => {
                let next = prev;

                if (utilityPrefill && hasUtilityPrefill(utilityPrefill)) {
                    const utilityPatch = mergeUtilityPrefill(next, utilityPrefill);
                    if (Object.keys(utilityPatch).length > 0) {
                        if (!cancelled) setUtilityFromOpportunity(true);
                        next = { ...next, ...utilityPatch };
                    }
                }

                if (
                    opportunityContext.productPrefill &&
                    hasProductPrefill(opportunityContext.productPrefill)
                ) {
                    const productPatch = mergeProductPrefill(
                        next,
                        opportunityContext.productPrefill,
                        { replacePrimaryProducts: !hadSavedIntakeFormRef.current },
                    );
                    if (Object.keys(productPatch).length > 0) {
                        next = { ...next, ...productPatch };
                    }
                }

                return next;
            });

            const selectedProducts =
                opportunityContext.productPrefill &&
                hasProductPrefill(opportunityContext.productPrefill);
            if (!selectedProducts) return;

            const design = await ensureSystemDesignForPrefill(site.salesForceId);
            if (cancelled || !design) return;

            setForm((prev) => {
                const batteryPatch = mergeBatteryPrefillFromSystemDesign(prev, design);
                if (Object.keys(batteryPatch).length === 0) return prev;
                return { ...prev, ...batteryPatch };
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [site.salesForceId]);

    const roofDesignDrawerOpen =
        activePanel === 'design' &&
        form.products.Roof &&
        designProductFocus !== 'Battery';

    useEffect(() => {
        if (!roofDesignDrawerOpen) {
            setDesignDrawerFooter(null);
        }
    }, [roofDesignDrawerOpen]);

    useEffect(() => {
        if (!roofDesignDrawerOpen) return undefined;

        let cancelled = false;
        void (async () => {
            const oppId =
                resolveStoreOpportunityId() ?? (await hydrateOpportunityStore(site.salesForceId));
            if (cancelled || !oppId) return;

            if (useOneButtonStore.getState().systemDesign) return;

            try {
                const design = await designServices.RetrieveSystemDesign(oppId);
                if (!cancelled && design) setSystemDesign(design);
            } catch {
                /* roof save still works once design loads elsewhere */
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [roofDesignDrawerOpen, site.salesForceId, setSystemDesign]);

    useEffect(() => {
        if (!form.products.Roof && !form.products.Solar && !form.products.Battery) return undefined;

        let cancelled = false;
        void (async () => {
            const design = await ensureSystemDesignForPrefill(site.salesForceId);
            if (cancelled || !design) return;

            setForm((prev) => {
                const batteryPatch = mergeBatteryPrefillFromSystemDesign(prev, design);
                if (Object.keys(batteryPatch).length === 0) return prev;
                return { ...prev, ...batteryPatch };
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [
        form.products.Battery,
        form.products.Roof,
        form.products.Solar,
        site.salesForceId,
    ]);

    useEffect(() => {
        persistIntakeForm(site.salesForceId, form);
    }, [form, site.salesForceId]);

    useEffect(() => {
        if (!viewportPopupOpen) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setViewportPopupOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [viewportPopupOpen]);

    useEffect(() => {
        if (!solarLayoutPopupOpen) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setSolarLayoutPopupOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [solarLayoutPopupOpen]);

    useEffect(() => {
        if (!aiDesignsPopupOpen) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setAiDesignsPopupOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [aiDesignsPopupOpen]);

    useEffect(() => {
        if (!productPickerOpen) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setProductPickerOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [productPickerOpen]);

    useEffect(() => {
        if (!form.products.Solar) {
            setSolarLayoutPopupOpen(false);
        }
    }, [form.products.Solar]);

    const updateUtilityPopoverPosition = useCallback(() => {
        const button = utilityButtonRef.current;
        if (!button) return;

        const buttonRect = button.getBoundingClientRect();
        const popoverWidth = utilityPopoverRef.current?.offsetWidth ?? UTILITY_POPOVER_WIDTH;
        const popoverHeight = utilityPopoverRef.current?.offsetHeight ?? 289;

        let left = buttonRect.left + buttonRect.width / 2 - popoverWidth / 2;
        const maxLeft = window.innerWidth - popoverWidth - VIEWPORT_MARGIN;
        left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

        const spaceBelow = window.innerHeight - buttonRect.bottom - UTILITY_POPOVER_GAP - VIEWPORT_MARGIN;
        const spaceAbove = buttonRect.top - UTILITY_POPOVER_GAP - VIEWPORT_MARGIN;
        const placement: 'below' | 'above' =
            spaceBelow >= popoverHeight || spaceBelow >= spaceAbove ? 'below' : 'above';

        let top =
            placement === 'below'
                ? buttonRect.bottom + UTILITY_POPOVER_GAP
                : buttonRect.top - popoverHeight - UTILITY_POPOVER_GAP;

        const maxTop = window.innerHeight - popoverHeight - VIEWPORT_MARGIN;
        top = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));

        setUtilityPopoverPos({ top, left, placement });
    }, []);

    const updateDesignToolPopoverPosition = useCallback(() => {
        const button = designToolButtonRef.current;
        if (!button) return;

        const buttonRect = button.getBoundingClientRect();
        const popoverWidth = designToolPopoverRef.current?.offsetWidth ?? UTILITY_POPOVER_WIDTH;
        const popoverHeight = designToolPopoverRef.current?.offsetHeight ?? 220;

        let left = buttonRect.left + buttonRect.width / 2 - popoverWidth / 2;
        const maxLeft = window.innerWidth - popoverWidth - VIEWPORT_MARGIN;
        left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

        const spaceBelow = window.innerHeight - buttonRect.bottom - UTILITY_POPOVER_GAP - VIEWPORT_MARGIN;
        const spaceAbove = buttonRect.top - UTILITY_POPOVER_GAP - VIEWPORT_MARGIN;
        const placement: 'below' | 'above' =
            spaceBelow >= popoverHeight || spaceBelow >= spaceAbove ? 'below' : 'above';

        let top =
            placement === 'below'
                ? buttonRect.bottom + UTILITY_POPOVER_GAP
                : buttonRect.top - popoverHeight - UTILITY_POPOVER_GAP;

        const maxTop = window.innerHeight - popoverHeight - VIEWPORT_MARGIN;
        top = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));

        setDesignToolPopoverPos({ top, left, placement });
    }, []);

    useLayoutEffect(() => {
        if (!utilityPopoverOpen) {
            setUtilityPopoverPos(null);
            return;
        }
        updateUtilityPopoverPosition();
        const frame = requestAnimationFrame(() => updateUtilityPopoverPosition());
        return () => cancelAnimationFrame(frame);
    }, [utilityPopoverOpen, updateUtilityPopoverPosition]);

    useLayoutEffect(() => {
        if (!designToolPopoverOpen) {
            setDesignToolPopoverPos(null);
            return;
        }
        updateDesignToolPopoverPosition();
        const frame = requestAnimationFrame(() => updateDesignToolPopoverPosition());
        return () => cancelAnimationFrame(frame);
    }, [designToolPopoverOpen, updateDesignToolPopoverPosition]);

    useEffect(() => {
        if (!utilityPopoverOpen) return undefined;

        const onDocClick = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (utilityWrapRef.current?.contains(target)) return;
            if (utilityPopoverRef.current?.contains(target)) return;
            setUtilityPopoverOpen(false);
        };
        const onReflow = () => updateUtilityPopoverPosition();

        const attachListenerTimer = window.setTimeout(() => {
            document.addEventListener('click', onDocClick, true);
        }, 0);
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true);

        return () => {
            window.clearTimeout(attachListenerTimer);
            document.removeEventListener('click', onDocClick, true);
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [utilityPopoverOpen, updateUtilityPopoverPosition]);

    useEffect(() => {
        if (!designToolPopoverOpen) return undefined;

        const onDocClick = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (designToolButtonRef.current?.contains(target)) return;
            if (designToolPopoverRef.current?.contains(target)) return;
            setDesignToolPopoverOpen(false);
        };
        const onReflow = () => updateDesignToolPopoverPosition();

        document.addEventListener('mousedown', onDocClick);
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true);

        return () => {
            document.removeEventListener('mousedown', onDocClick);
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [designToolPopoverOpen, updateDesignToolPopoverPosition]);

    useEffect(() => {
        if (!activePanel) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [activePanel]);

    useEffect(() => {
        if (site.salesForceId?.trim()) {
            setDesignViewerKept(true);
        }
    }, [site.salesForceId]);

    const syncSelectionToEmbed = useCallback(() => {
        if (siteRoofs.length) {
            const includedTagIds = siteRoofs
                .filter((_, index) => form.roofSelections[index] !== false)
                .map((r) => r.roofTagId);
            postPciRoofSelectionToEmbed(includedTagIds);
        }
        const includedModuleIds = collectIncludedModuleIds(form.roofModuleLayouts);
        const hasLiveModules = form.roofModuleLayouts.some((layout) =>
            layout.modules.some((cell) => cell != null),
        );
        // Do not post [] before live layouts exist — that hid every module in the embed.
        // Once layouts exist, post even an empty list so Active/deselect-all hides 3D panels.
        if (hasLiveModules) {
            postPciModuleSelectionToEmbed(includedModuleIds);
        }
        if (form.siteTrees.length > 0) {
            // Module step: only preview ghosts while Tree Cutting tab is open.
            const ghostIds =
                !treeCuttingPreviewActive && boardStep < 3
                    ? []
                    : form.siteTrees.filter((tree) => tree.ghosted).map((tree) => tree.id);
            const ghostKey =
                !treeCuttingPreviewActive && boardStep < 3
                    ? '__pci-no-tree-preview__'
                    : pciGhostedTreeIdKey(form.siteTrees);
            if (ghostKey !== lastPostedGhostKeyRef.current) {
                lastPostedGhostKeyRef.current = ghostKey;
                postPciTreeGhostToEmbed(ghostIds);
            }
        }
    }, [
        siteRoofs,
        form.roofSelections,
        form.roofModuleLayouts,
        form.siteTrees,
        boardStep,
        treeCuttingPreviewActive,
    ]);
    const syncSelectionToEmbedRef = useRef(syncSelectionToEmbed);
    syncSelectionToEmbedRef.current = syncSelectionToEmbed;

    const scrollToSection = useCallback((sectionId: string) => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const moduleViewportEligible =
        designViewerKept &&
        hasActiveDesignTool(form) &&
        ((form.preferredDesignTool === 'aurora' && form.auroraAvailable) ||
            (form.preferredDesignTool === 'hover' && form.hoverAvailable));
    const solarLayoutPopupActive =
        hasActiveDesignTool(form) && form.products.Solar && solarLayoutPopupOpen;
    const moduleViewportActive = moduleViewportEligible && solarLayoutPopupActive;
    const viewportPopupActive = designViewerKept && viewportPopupOpen;
    /** Exactly one DOM slot owns the shared iframe at a time (popup wins over inline module). */
    const designViewerSlotMode: 'popup' | 'module' | null = viewportPopupActive
        ? 'popup'
        : moduleViewportActive
          ? 'module'
          : null;
    const viewportDockActive = designViewerSlotMode != null;
    /** Float above Design drawer; otherwise sit inside the stack/popup slot. */
    const dockHostElevated = viewportDockActive && activePanel === 'design' && !viewportPopupActive;
    const dockHostInSlot = designViewerDocked && Boolean(dockSlotEl) && !dockHostElevated;

    /**
     * Keep a single iframe instance for the whole visit.
     * Prefer portaling into the module/popup slot so the canvas scrolls with the stack;
     * fall back to fixed overlay on `.app` when elevated over the Design drawer.
     */
    useLayoutEffect(() => {
        if (!viewportDockActive) {
            setDesignViewerDocked(false);
            setDockSlotEl(null);
            return undefined;
        }

        let cancelled = false;
        let slotWaitRaf = 0;
        let scrollRaf = 0;
        let resizeTimer = 0;
        let observedSlot: HTMLElement | null = null;
        let observer: ResizeObserver | null = null;
        let lastPostedSize = '';

        const resolveDockMode = (): 'fixed' | 'in-slot' =>
            activePanel === 'design' && !viewportPopupActive ? 'fixed' : 'in-slot';

        const applyDockStyles = (next: DesignViewerDockRect) => {
            const host = designViewerHostRef.current;
            if (!host) return;
            applyPciDockHostStyles(host, next, resolveDockMode());
        };

        const nudgeEmbedResize = (next: DesignViewerDockRect) => {
            const sizeKey = `${next.width}x${next.height}`;
            if (sizeKey === lastPostedSize) return;
            lastPostedSize = sizeKey;
            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                postPciViewportResizeToEmbed();
                if (viewportPopupActive) {
                    postPci3dViewToEmbed();
                }
                syncSelectionToEmbedRef.current();
            }, 50);
        };

        const updateDock = () => {
            if (cancelled) return;
            const slot = designViewerSlotRef.current;
            if (!slot) {
                slotWaitRaf = window.requestAnimationFrame(updateDock);
                return;
            }
            setDockSlotEl((prev) => (prev === slot ? prev : slot));
            if (observer && observedSlot !== slot) {
                if (observedSlot) observer.unobserve(observedSlot);
                observer.observe(slot);
                observedSlot = slot;
            }

            const rect = slot.getBoundingClientRect();
            if (rect.width < 8 || rect.height < 8) {
                slotWaitRaf = window.requestAnimationFrame(updateDock);
                return;
            }
            const headerH = getAppUnifiedHeaderHeight();
            let left = rect.left;
            let width = rect.width;
            // Keep the docked canvas out from under the pricing sidepanel (desktop only).
            if (pricingPanelOpen && resolveDockMode() === 'fixed') {
                const panel = document.querySelector('.pci-pricing-sidepanel');
                if (panel) {
                    const panelRect = panel.getBoundingClientRect();
                    const isBottomSheet =
                        panelRect.width >= window.innerWidth * 0.9 &&
                        panelRect.top > headerH + 40;
                    if (!isBottomSheet) {
                        const panelLeft = panelRect.left;
                        if (Number.isFinite(panelLeft) && panelLeft < rect.right) {
                            width = Math.max(8, panelLeft - rect.left - 12);
                        }
                    }
                }
            }
            const next: DesignViewerDockRect = {
                left,
                top: rect.top,
                width,
                height: rect.height,
                clipTop:
                    resolveDockMode() === 'fixed' && !viewportPopupActive
                        ? Math.max(0, headerH - rect.top)
                        : 0,
            };
            lastDesignViewerDockRectRef.current = next;
            applyDockStyles(next);
            setDesignViewerDocked(true);
            nudgeEmbedResize(next);
        };

        const onScrollOrResize = () => {
            if (scrollRaf) return;
            scrollRaf = window.requestAnimationFrame(() => {
                scrollRaf = 0;
                updateDock();
            });
        };

        observer =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => onScrollOrResize())
                : null;

        updateDock();
        const settleTimers = [50, 120, 240, 400, 700].map((ms) =>
            window.setTimeout(updateDock, ms),
        );

        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(slotWaitRaf);
            window.cancelAnimationFrame(scrollRaf);
            window.clearTimeout(resizeTimer);
            settleTimers.forEach((id) => window.clearTimeout(id));
            observer?.disconnect();
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
            const host = designViewerHostRef.current;
            if (host) {
                host.style.left = '';
                host.style.top = '';
                host.style.width = '';
                host.style.height = '';
                host.style.clipPath = '';
            }
            setDesignViewerDocked(false);
            setDockSlotEl(null);
        };
    }, [activePanel, boardStep, viewportDockActive, viewportPopupActive, pricingPanelOpen]);

    useLayoutEffect(() => {
        if (!designViewerDocked) return;
        const next = lastDesignViewerDockRectRef.current;
        const host = designViewerHostRef.current;
        if (!next || !host) return;
        applyPciDockHostStyles(host, next, dockHostInSlot ? 'in-slot' : 'fixed');
    });

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as {
                type?: string;
                url?: string;
                message?: string;
                roofs?: PciSiteRoof[];
                layouts?: PreContractRoofModuleLayout[];
                trees?: PciSiteTree[];
            } | null;
            if (!data?.type) return;

            if (data.type === 'pci-onedraw-layout-snapshot') {
                return;
            }

            if (data.type === PCI_ONEDRAW_LOAD_ERROR_MSG) {
                const message = data.message?.trim() || DEFAULT_SITE_VIEWER_MESSAGE;
                setSiteViewportError(message);
                return;
            }

            if (data.type === PCI_ONEDRAW_ROOFS_MSG && Array.isArray(data.roofs)) {
                const roofs = data.roofs.filter(
                    (r) => r && typeof r.roofTagId === 'number' && Number.isFinite(r.roofTagId),
                );
                if (!roofs.length) return;
                setSiteViewportError(null);
                const liveLayouts = Array.isArray(data.layouts)
                    ? (data.layouts as PreContractRoofModuleLayout[]).filter(
                          (layout) =>
                              layout &&
                              typeof layout.cols === 'number' &&
                              Array.isArray(layout.modules) &&
                              layout.modules.some((cell) => cell != null),
                      )
                    : [];
                setSiteRoofs((prevRoofs) =>
                    pciSiteRoofsEqual(prevRoofs, roofs) ? prevRoofs : roofs,
                );
                setForm((prev) => {
                    const nextSelections = roofs.map((_, index) =>
                        prev.roofSelections[index] !== false,
                    );
                    const hasPrevLive = prev.roofModuleLayouts.some((layout) =>
                        layout.modules.some((cell) => cell != null),
                    );
                    const roofModuleLayouts =
                        liveLayouts.length > 0
                            ? mergeLiveModuleLayouts(
                                  prev.roofModuleLayouts,
                                  liveLayouts,
                                  nextSelections,
                              )
                            : hasPrevLive
                              ? applyRoofSelectionsToLayouts(prev.roofModuleLayouts, nextSelections)
                              : prev.roofModuleLayouts;
                    const incomingTrees = Array.isArray(data.trees) ? data.trees : [];
                    const mergedTrees = mergePciSiteTrees(prev.siteTrees, incomingTrees);
                    const siteTrees = pciSiteTreesLookEqual(prev.siteTrees, mergedTrees)
                        ? prev.siteTrees
                        : mergedTrees;
                    const selectionsUnchanged = nextSelections.every(
                        (selected, index) => prev.roofSelections[index] !== false === selected,
                    );
                    if (
                        selectionsUnchanged &&
                        pciRoofModuleLayoutsEqual(prev.roofModuleLayouts, roofModuleLayouts) &&
                        siteTrees === prev.siteTrees
                    ) {
                        return prev;
                    }
                    return {
                        ...prev,
                        roofSelections: nextSelections,
                        roofModuleLayouts,
                        siteTrees,
                        treeCount: siteTrees.length || prev.treeCount,
                    };
                });
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    useEffect(() => {
        syncSelectionToEmbed();
    }, [syncSelectionToEmbed]);

    /** Keep Design-drawer slope totals in sync with viewer Roof Square Table + selected roofs. */
    useEffect(() => {
        const squares = sumPciSelectedRoofSquares(siteRoofs, form.roofSelections);
        if (squares.total <= 0) return;
        setForm((prev) => {
            if (
                prev.roofSlopeLowTotal === squares.low &&
                prev.roofSlopeNormalTotal === squares.standard
            ) {
                return prev;
            }
            return {
                ...prev,
                roofSlopeLowTotal: squares.low,
                roofSlopeNormalTotal: squares.standard,
            };
        });
    }, [siteRoofs, form.roofSelections]);

    useEffect(() => {
        if (!form.products.Roof) return;
        setForm((prev) => {
            const snapshot = buildPreContractRoofDesignSnapshot({
                form: prev,
                systemDesign,
                priceQuoteOverrides,
            });
            const patch = patchFormFromRoofDesignSnapshot(
                prev,
                snapshot,
                priceQuoteOverrides,
                systemDesign,
            );
            if (!patch) return prev;
            return { ...prev, ...patch };
        });
    }, [form.products.Roof, priceQuoteOverrides, systemDesign]);

    useEffect(() => {
        setForm((prev) => {
            if (prev.preferredDesignTool === 'aurora' && !prev.auroraAvailable) {
                return { ...prev, preferredDesignTool: null };
            }
            if (prev.preferredDesignTool === 'hover' && !prev.hoverAvailable) {
                return { ...prev, preferredDesignTool: null };
            }
            return prev;
        });
    }, [form.auroraAvailable, form.hoverAvailable]);

    const patchForm = useCallback((patch: Partial<PreContractIntakeForm>) => {
        setForm((prev) => ({ ...prev, ...patch }));
    }, []);

    const openPanel = (panelId: PanelId) => {
        setUtilityPopoverOpen(false);
        setDesignToolPopoverOpen(false);
        setActivePanel(panelId);
    };

    const closePanel = () => {
        setActivePanel(null);
        setDesignProductFocus(null);
    };

    const toggleUtilityPopover = (event?: React.MouseEvent) => {
        event?.stopPropagation();
        setActivePanel(null);
        setDesignToolPopoverOpen(false);
        setUtilityPopoverOpen((open) => !open);
    };

    const toggleDesignToolPopover = () => {
        setActivePanel(null);
        setUtilityPopoverOpen(false);
        setDesignToolPopoverOpen((open) => !open);
    };

    const selectDesignTool = (tool: PreContractDesignTool) => {
        const available = tool === 'aurora' ? form.auroraAvailable : form.hoverAvailable;
        if (!available) return;
        patchForm({ preferredDesignTool: tool });
        setDesignToolPopoverOpen(false);
    };

    const restorePreAiSnapshot = () => {
        const snap = preAiSnapshotRef.current;
        if (!snap) return;
        patchForm({
            roofModuleLayouts: snap.roofModuleLayouts,
            roofSelections: snap.roofSelections,
            systemUsageKwh: snap.systemUsageKwh,
        });
    };

    const goBackFromBoard = () => {
        if (boardStep === 4) {
            setPricingPanelOpen(false);
            setBoardStep(3);
            setAiDesignsPopupOpen(true);
            return;
        }
        if (boardStep === 3) {
            setPricingPanelOpen(false);
            setAiDesignsPopupOpen(false);
            restorePreAiSnapshot();
            setBoardStep(2);
            setSolarLayoutPopupOpen(true);
            return;
        }
        if (boardStep === 2) {
            setPricingPanelOpen(false);
            setBoardStep(1);
            setTreeCuttingPreviewActive(false);
            scrollToSection('pci-unified-stack');
            return;
        }
        navigate('/sites');
    };

    const handleRunAiDesign = (retainPrevious = false) => {
        if (!canRunAiDesigns(form)) {
            setHighlightValidation(true);
            const firstIssue = TILE_STEPS.find(
                (stepId) => getStepValidation(stepId, form).status !== 'complete',
            );
            if (firstIssue) {
                focusIncompleteStep(firstIssue);
            }
            return;
        }
        persistIntakeForm(site.salesForceId, form);
        preAiSnapshotRef.current = {
            roofModuleLayouts: form.roofModuleLayouts,
            roofSelections: form.roofSelections,
            systemUsageKwh: form.systemUsageKwh,
        };
        if (!retainPrevious) {
            clearPreContractAiOptionsCache();
            setAiDesignRunId((n) => n + 1);
        }
        setPricingPanelOpen(false);
        setBoardStep(3);
        setSolarLayoutPopupOpen(false);
        setAiDesignsPopupOpen(true);
    };

    const goToModuleStep = () => {
        const titleReady = !titleLoading && (isTitleInfoAvailable(titleInfo) || titleContactSynced);
        if (!canAdvanceFromIntakeBoard(form, titleReady)) {
            setHighlightValidation(true);
            return;
        }
        setUtilityPopoverOpen(false);
        setDesignToolPopoverOpen(false);
        setViewportPopupOpen(false);
        setActivePanel(null);
        setForm((prev) => {
            const roofs =
                siteRoofs.length > 0
                    ? siteRoofs.map((r) => ({ roofTagId: r.roofTagId, label: r.label }))
                    : undefined;
            // Keep Design drawer roof inclusion; fill modules on every included roof.
            const roofSelections =
                roofs && roofs.length > 0
                    ? roofs.map((_, index) => prev.roofSelections[index] !== false)
                    : maxFillRoofSelections();
            const hasLiveLayouts = prev.roofModuleLayouts.some((layout) =>
                layout.modules.some((cell) => cell != null),
            );
            return {
                ...prev,
                roofSelections,
                treeCount: estimateTreeCount(site.salesForceId),
                roofModuleLayouts: hasLiveLayouts
                    ? applyRoofSelectionsToLayouts(prev.roofModuleLayouts, roofSelections)
                    : buildRoofModuleLayouts(roofSelections, {
                          maxFill: true,
                          roofs,
                      }),
            };
        });
        setBoardStep(2);
        scrollToSection('pci-section-layout');
    };

    useEffect(() => {
        if (!hasActiveDesignTool(form)) return;
        setForm((prev) => {
            const hasLiveLayouts = prev.roofModuleLayouts.some((layout) =>
                layout.modules.some((cell) => cell != null),
            );
            if (hasLiveLayouts) return prev;
            const roofs =
                siteRoofs.length > 0
                    ? siteRoofs.map((r) => ({ roofTagId: r.roofTagId, label: r.label }))
                    : undefined;
            const roofSelections =
                roofs && roofs.length > 0
                    ? roofs.map((_, index) => prev.roofSelections[index] !== false)
                    : maxFillRoofSelections();
            return {
                ...prev,
                roofSelections,
                treeCount: estimateTreeCount(site.salesForceId),
                roofModuleLayouts: buildRoofModuleLayouts(roofSelections, {
                    maxFill: true,
                    roofs,
                }),
            };
        });
    }, [form.preferredDesignTool, form.auroraAvailable, form.hoverAvailable, siteRoofs, site.salesForceId]);

    const applyAiOptionToEmbed = useCallback((option: PreContractAiDesignOption) => {
        const includedModuleIds = collectIncludedModuleIds(option.layouts);
        if (includedModuleIds.length === 0) return;
        postPciApplyDesignOptionToEmbed({
            includedModuleIds,
            modulesLayout: option.modulesLayout ?? null,
            ghostedTreeIds: option.ghostedTreeIds ?? [],
        });
        if (siteRoofs.length) {
            const includedTagIds = siteRoofs
                .filter((_, index) => option.roofSelections[index] !== false)
                .map((roof) => roof.roofTagId);
            if (includedTagIds.length > 0) {
                postPciRoofSelectionToEmbed(includedTagIds);
            }
        }
    }, [siteRoofs]);

    const openViewportPopup = (option?: PreContractAiDesignOption | null) => {
        setUtilityPopoverOpen(false);
        setDesignToolPopoverOpen(false);
        setActivePanel(null);
        setViewportPopupOpen(true);
        // Guard: header buttons pass a click event if wired as onClick={() => openViewportPopup()}.
        const fromArg =
            option &&
            typeof option === 'object' &&
            Array.isArray((option as PreContractAiDesignOption).layouts)
                ? (option as PreContractAiDesignOption)
                : null;
        const target = fromArg ?? (boardStep >= 3 ? resolveSelectedPreContractAiOption() : null);
        if (!target) {
            postPci3dViewToEmbed();
            return;
        }
        const apply = () => applyAiOptionToEmbed(target);
        apply();
        [80, 220, 500, 1000].forEach((ms) => window.setTimeout(apply, ms));
    };

    useEffect(() => {
        if (!viewportPopupActive) return undefined;
        const option = boardStep >= 3 ? resolveSelectedPreContractAiOption() : null;
        const apply = () => {
            if (option) applyAiOptionToEmbed(option);
            else postPci3dViewToEmbed();
        };
        apply();
        const timers = [80, 220, 500, 1000].map((ms) => window.setTimeout(apply, ms));
        return () => timers.forEach((id) => window.clearTimeout(id));
    }, [viewportPopupActive, boardStep, applyAiOptionToEmbed]);

    const closeViewportPopup = () => {
        setViewportPopupOpen(false);
    };

    const closeSolarLayoutPopup = () => {
        if (
            form.products.Solar &&
            isSolarLayoutModifiedFromBaseline(form, savedSolarBaselineRef.current)
        ) {
            patchForm({
                solarDesignRevision: 'manual',
                appliedAiDesignTitle: null,
            });
        }
        setSolarLayoutPopupOpen(false);
    };

    const openSolarLayoutPopup = () => {
        if (!designToolReady) return;
        patchForm({
            products: {
                ...form.products,
                Solar: true,
            },
        });
        setSolarLayoutPopupOpen(true);
    };

    const closeAiDesignsPopup = () => {
        setAiDesignsPopupOpen(false);
    };

    const openAiDesignsPopup = () => {
        if (!designToolReady || !(boardStep >= 3 || hasPreviousAiDesigns)) return;
        setAiDesignsPopupOpen(true);
    };

    const handleApplyAiDesign = useCallback(
        (option: PreContractAiDesignOption) => {
            const patch = {
                ...intakePatchFromAiDesign(option, form.siteTrees),
                solarDesignRevision: 'ai' as const,
                appliedAiDesignTitle: option.title?.trim() || `Design ${option.rank}`,
            };
            const nextForm = { ...form, ...patch };
            patchForm(patch);
            persistIntakeForm(site.salesForceId, nextForm);
            applyAiOptionToEmbed(option);
            setAiDesignsPopupOpen(false);
            setSolarLayoutPopupOpen(false);
            setPricingPanelOpen(false);
            setBoardStep(1);
            window.setTimeout(() => scrollToSection('pci-unified-deal'), 0);
        },
        [applyAiOptionToEmbed, form, patchForm, site.salesForceId],
    );

    const handleAiDesignsContinue = () => {
        const option = resolveSelectedPreContractAiOption();
        if (option) {
            handleApplyAiDesign(option);
            return;
        }
        setAiDesignsPopupOpen(false);
        setPricingPanelOpen(false);
        setBoardStep(4);
        scrollToSection('pci-section-selected');
    };

    const handleTitleSyncSuccess = useCallback(() => {
        void refreshTitleContext({ silent: true });
    }, [refreshTitleContext]);

    const openAddTitleContactsModal = useCallback(async () => {
        setUtilityPopoverOpen(false);
        setDesignToolPopoverOpen(false);
        setActivePanel(null);
        if (!useOneButtonStore.getState().opportunityDetails) {
            await refreshTitleContext();
        }
        setAddTitleContactsModalOpen(true);
    }, [refreshTitleContext]);

    const focusIncompleteStep = (stepId: WizardStepId) => {
        const target = stepToPanel(stepId);
        if (target === 'utility') {
            setUtilityPopoverOpen(true);
            setDesignToolPopoverOpen(false);
            setActivePanel(null);
            return;
        }
        if (target === 'designTool') {
            setDesignToolPopoverOpen(true);
            setUtilityPopoverOpen(false);
            setActivePanel(null);
            return;
        }
        if (target) {
            openPanel(target);
        }
    };

    const designIssues = useMemo(() => designAvailabilityIssues(form), [form]);
    const installDisplay = formatInstallAddress(form) || site.address;
    const allComplete = canLaunchWorkspace(form);
    const canRunAi = canRunAiDesigns(form);
    const solarDesignModified = useMemo(() => {
        if (!form.products.Solar) return false;
        return (
            form.solarDesignRevision != null ||
            isSolarLayoutModifiedFromBaseline(form, savedSolarBaselineRef.current)
        );
    }, [form]);
    const designToolReady = hasActiveDesignTool(form);
    const hasPreviousAiDesigns = hasPreContractAiOptionsCache();
    const showPricingPreview = designToolReady && !form.products.Solar;
    const showSolarLayoutStatus = designToolReady && form.products.Solar;
    const showAiSection = designToolReady && (boardStep >= 3 || hasPreviousAiDesigns);
    const showSelectedSection = boardStep >= 4;
    const aiDesignsPopupActive = showAiSection && aiDesignsPopupOpen;
    const designDrawerTitle =
        activePanel === 'design'
            ? designProductFocus === 'Roof'
                ? 'Roof design'
                : designProductFocus === 'Battery'
                  ? 'Battery design'
                  : roofDesignDrawerOpen
                    ? 'Product designs'
                    : PANEL_LABELS.design
            : activePanel
              ? PANEL_LABELS[activePanel]
              : '';
    const siteViewportHasRoofs = siteRoofs.length > 0;
    const siteViewportHasModules = form.roofModuleLayouts.some((layout) =>
        layout.modules.some((cell) => cell != null),
    );
    const siteViewportLoadingMessage =
        siteViewportError || !designToolReady
            ? null
            : !siteViewportHasRoofs
              ? '3D viewport for this site is loading. Roof data will appear when the model is ready.'
              : !siteViewportHasModules
                ? '3D viewport loaded — finishing module layout from the site model…'
                : null;
    const designToolLabel =
        form.preferredDesignTool === 'hover'
            ? 'Hover'
            : form.preferredDesignTool === 'aurora'
              ? 'Aurora'
              : 'Design tool';
    const summaryItems = useMemo(() => {
        const viewportLoading = Boolean(siteViewportLoadingMessage);
        const systemKw = preContractSystemSizeKw(form);
        const systemUsage =
            form.systemUsageKwh.trim() ||
            (() => {
                const estimated = estimateLayoutAnnualKwh({
                    layouts: form.roofModuleLayouts,
                    roofSelections: form.roofSelections,
                    moduleWattage: form.moduleTypeWattage,
                });
                return estimated != null && estimated > 0 ? String(estimated) : '';
            })();
        return [
            {
                label: 'Utility Usage',
                value: form.annualUsageKwh.trim()
                    ? `${form.annualUsageKwh.trim()} kWh`
                    : '—',
            },
            {
                label: 'System Size',
                value: systemKw > 0 ? `${systemKw.toFixed(2)} kW` : '—',
            },
            {
                label: 'System Production',
                value: siteViewportError
                    ? 'Unavailable'
                    : viewportLoading
                      ? 'Loading'
                      : systemUsage
                        ? `${systemUsage} kWh`
                        : '—',
                loading: viewportLoading,
                error: Boolean(siteViewportError),
            },
            {
                label: 'All-in Rate',
                value: form.utilityRate.trim()
                    ? `$${form.utilityRate.trim()}/kWh`
                    : '—',
            },
        ];
    }, [
        form.annualUsageKwh,
        form.systemUsageKwh,
        form.moduleTypeWattage,
        form.roofModuleLayouts,
        form.roofSelections,
        form.utilityRate,
        siteViewportError,
        siteViewportLoadingMessage,
    ]);

    /** Keep System Usage in sync when included panels / wattage change (not on every embed republish). */
    const systemUsageSyncKeyRef = useRef<string>('');
    useEffect(() => {
        const estimated = estimateLayoutAnnualKwh({
            layouts: form.roofModuleLayouts,
            roofSelections: form.roofSelections,
            moduleWattage: form.moduleTypeWattage,
        });
        if (estimated == null) return;
        const systemKw = preContractSystemSizeKw(form);
        const { count } = countIncludedModules(form.roofModuleLayouts, form.roofSelections);
        const syncKey = `${count}|${form.moduleTypeWattage ?? 0}|${systemKw}`;
        const next = estimated > 0 ? String(estimated) : '';
        if (systemUsageSyncKeyRef.current === syncKey) return;
        systemUsageSyncKeyRef.current = syncKey;
        setForm((prev) => {
            if (prev.systemUsageKwh === next) return prev;
            return { ...prev, systemUsageKwh: next };
        });
    }, [form.roofModuleLayouts, form.roofSelections, form.moduleTypeWattage]);

    useEffect(() => {
        if (allComplete) {
            setHighlightValidation(false);
        }
    }, [allComplete]);

    const handleLaunch = (action: PreContractLaunchAction, formOverride?: PreContractIntakeForm) => {
        const payloadForm = formOverride ?? form;
        if (!canLaunchWorkspace(payloadForm)) {
            setHighlightValidation(true);
            const firstIssue = TILE_STEPS.find(
                (stepId) => getStepValidation(stepId, payloadForm).status !== 'complete',
            );
            if (firstIssue) {
                focusIncompleteStep(firstIssue);
            }
            return;
        }
        persistIntakeForm(site.salesForceId, payloadForm);
        persistDashboardLayoutVariant('v2');
        sessionStorage.setItem('SalesForceId', site.salesForceId);
        sessionStorage.setItem('currentStage', String(parseInt(site.stage.split(':')[0], 10) || 0));
        markPreContractLaunch(action);
        if (action === 'openDesign') {
            markPreContractReturn(4);
        } else if (action === 'runAi') {
            markPreContractReturn(2);
        }
        useOneButtonStore.getState().setSalesForceId(String(site.salesForceId));

        const openCanvas = action === 'runAi' || action === 'openDesign';
        useViewerStore.setState({
            dashboardLayoutVariant: 'v2',
            treeCartItems: [],
            pathname: openCanvas ? '/configure/roof' : '/configure/modules',
            isDashboardOpen: !openCanvas,
            siteViewerCalculationsPending: false,
            viewerAlert: {
                show: false,
                title: '',
                message: '',
                messageType: AlertType.info,
                isModal: false,
            },
            isStreetViewVisible: false,
            loadingCompleted: false,
            siteLoadingError: null,
            siteMultipliersPendingNotice: null,
            siteViewerUnavailablePrompt: null,
        });

        const query = new URLSearchParams({ sfid: site.salesForceId });
        if (openCanvas) query.set('view', 'onedraw');
        if (action === 'runPricing') query.set('section', 'financing');
        navigate(`/configure?${query.toString()}`);
    };

    const utilityValidation = getStepValidation('utility', form);
    const designValidation = getStepValidation('design', form);
    const isEnergyCommunity = useMemo(
        () => resolveEnergyCommunityLikelyQualifies(opportunityDetails, energyCommunityStatus),
        [opportunityDetails, energyCommunityStatus],
    );
    const titleInfoAvailable = isTitleInfoAvailable(titleInfo) || titleContactSynced;
    const showTitleActions = !titleLoading && !titleInfoAvailable;
    const showAddTitleButton = showTitleActions;
    const titleValidation: StepValidation = titleInfoAvailable
        ? { status: 'complete', label: 'Verified', issues: [] }
        : titleLoading
          ? { status: 'incomplete', label: 'Loading', issues: [] }
          : {
                status: 'incomplete',
                label: 'Required',
                issues: ['Title records are not available. Run Black Knight or add title contacts.'],
            };
    const canGoNext = canAdvanceFromIntakeBoard(form, titleValidation.status === 'complete');
    const nextBlockedReasons = [
        ...(designValidation.status !== 'complete' ? designValidation.issues : []),
        ...(utilityValidation.status !== 'complete' ? utilityValidation.issues : []),
        ...(titleValidation.status !== 'complete' ? titleValidation.issues : []),
    ];
    const activePanelValidation =
        activePanel === 'title'
            ? titleValidation
            : activePanel
              ? getStepValidation(PANEL_STEPS[activePanel], form)
              : null;
    const backAriaLabel =
        boardStep === 4
            ? 'Back to AI design options'
            : boardStep === 3
            ? 'Back to module layout'
            : boardStep === 2
              ? 'Back to intake'
              : 'Back to sites';
    const handleOpenPricingPanel = () => setPricingPanelOpen(true);

    const generateQuoteAccess = getOneButtonCapabilityValue(
        onebuttonCapabilities,
        'Generate_Quote_Access',
    );
    const isGenerateQuotePermissionDisabled =
        generateQuoteAccess !== 'Edit' && generateQuoteAccess !== 'View';

    const generateQuoteDisabledReason = useMemo(() => {
        if (isGenerateQuotePermissionDisabled) {
            return 'Generate Quote is not available for your role.';
        }
        if (!form.products.Solar && !form.products.Roof && !form.products.Battery) {
            return 'Select at least one product first.';
        }
        return undefined;
    }, [form.products.Battery, form.products.Roof, form.products.Solar, isGenerateQuotePermissionDisabled]);

    const handleGenerateQuoteFromPreview = useCallback(async () => {
        if (isGenerateQuotePermissionDisabled || generateQuoteLoading) return;

        const productTypes: Array<'Solar' | 'Roof' | 'Battery'> = [];
        if (form.products.Solar) productTypes.push('Solar');
        if (form.products.Roof) productTypes.push('Roof');
        if (form.products.Battery) productTypes.push('Battery');
        if (productTypes.length === 0) return;

        setGenerateQuoteLoading(true);
        try {
            const oppId =
                resolveStoreOpportunityId() ?? (await hydrateOpportunityStore(site.salesForceId));
            if (!oppId) {
                notification.warning({
                    message: 'Opportunity not loaded',
                    description: 'Wait for the opportunity to finish loading, then try again.',
                    placement: 'topRight',
                });
                return;
            }

            let opportunity = useOneButtonStore.getState().opportunityDetails;
            if (!opportunity) {
                await hydrateOpportunityStore(site.salesForceId);
                opportunity = useOneButtonStore.getState().opportunityDetails;
            }
            if (!opportunity) {
                notification.warning({
                    message: 'Opportunity not loaded',
                    description: 'Could not load opportunity details for quote generation.',
                    placement: 'topRight',
                });
                return;
            }

            const selectedQuotePayload = buildSelectedQuoteFromOpportunityProductTypes(
                productTypes,
                opportunity,
            );
            if (!selectedQuotePayload) {
                notification.warning({
                    message: 'Products not ready',
                    description:
                        'Active opportunity products with finance partners are required before generating a quote.',
                    placement: 'topRight',
                });
                return;
            }

            let priceQuote: typeof priceQuoteDetails | Awaited<
                ReturnType<typeof quoteServices.getRetrievePriceQuote>
            > = useOneButtonStore.getState().priceQuoteDetails;
            if (!priceQuote) {
                const fetched = await quoteServices.getRetrievePriceQuote(String(oppId));
                if (fetched) {
                    setPriceQuoteDetails(fetched as typeof priceQuoteDetails);
                    priceQuote = fetched;
                }
            }
            if (!priceQuote) {
                notification.warning({
                    message: 'Pricing not available',
                    description: 'Could not load price quote details for this opportunity.',
                    placement: 'topRight',
                });
                return;
            }

            setConfirmQuoteCellKey(selectedQuotePayload);
        } finally {
            setGenerateQuoteLoading(false);
        }
    }, [
        form.products.Battery,
        form.products.Roof,
        form.products.Solar,
        generateQuoteLoading,
        isGenerateQuotePermissionDisabled,
        setPriceQuoteDetails,
        site.salesForceId,
    ]);

    const selectedProducts = useMemo(
        () => PRE_CONTRACT_PRODUCTS.filter((product) => form.products[product]),
        [form.products],
    );

    const availableProductsToAdd = useMemo(
        () =>
            PRE_CONTRACT_PRODUCTS.filter(
                (product) =>
                    !form.products[product] && !PRE_CONTRACT_DISABLED_PRODUCTS.has(product),
            ),
        [form.products],
    );

    const openProductDesign = useCallback(
        (product: PreContractProduct) => {
            setProductPickerOpen(false);

            if (product === 'Solar') {
                setDesignProductFocus(null);
                setActivePanel(null);
                setAiDesignsPopupOpen(false);
                if (!designToolReady) return;
                setSolarLayoutPopupOpen(true);
                return;
            }

            if (product === 'Roof' || product === 'Battery') {
                setSolarLayoutPopupOpen(false);
                setAiDesignsPopupOpen(false);
                setDesignProductFocus(product);
                openPanel('design');
            }
        },
        [designToolReady, openPanel],
    );

    const addProductAndOpenDesign = useCallback(
        (product: PreContractProduct) => {
            if (PRE_CONTRACT_DISABLED_PRODUCTS.has(product) || form.products[product]) return;
            patchForm({
                products: {
                    ...form.products,
                    [product]: true,
                },
            });
            setProductPickerOpen(false);
            openProductDesign(product);
        },
        [form.products, openProductDesign, patchForm],
    );

    const removeProduct = useCallback(
        (product: PreContractProduct) => {
            const nextProducts = {
                ...form.products,
                [product]: false,
            };
            patchForm({ products: nextProducts });
            if (product === 'Solar') {
                setSolarLayoutPopupOpen(false);
            }
            if (activePanel === 'design') {
                const stillHasDesignProduct = nextProducts.Roof || nextProducts.Battery;
                if (!stillHasDesignProduct) {
                    closePanel();
                }
            }
        },
        [activePanel, closePanel, form.products, patchForm],
    );

    const handleGenerateQuoteComplete = useCallback(() => {
        setConfirmQuoteCellKey(null);
        setQuoteForm(null);
        setQuotesRefreshTrigger(Date.now());
    }, [setQuotesRefreshTrigger]);
    const handleOpenOneDrawFromAi = async (option: PreContractAiDesignOption) => {
        const patch = intakePatchFromAiDesign(option, form.siteTrees);
        const nextForm = { ...form, ...patch };
        patchForm(patch);
        const includedModuleIds = collectIncludedModuleIds(option.layouts);
        const includedRoofTagIds = option.layouts
            .filter((layout) => option.roofSelections[layout.roofIndex] !== false)
            .map((layout) => {
                if (typeof layout.roofTagId === 'number' && Number.isFinite(layout.roofTagId)) {
                    return layout.roofTagId;
                }
                return siteRoofs[layout.roofIndex]?.roofTagId;
            })
            .filter((id): id is number => typeof id === 'number' && Number.isFinite(id));
        postPciModuleSelectionToEmbed(includedModuleIds);
        const modulesLayout =
            option.modulesLayout && option.modulesLayout.length > 0
                ? option.modulesLayout
                : await requestPciModulesLayoutFromEmbed(includedModuleIds);
        const ghostedTreeIds = Array.from(
            new Set(
                (option.ghostedTreeIds ??
                    option.treeCut?.treeIds ??
                    nextForm.siteTrees.filter((tree) => tree.ghosted).map((tree) => tree.id)
                )
                    .map((id) => String(id).trim())
                    .filter(Boolean),
            ),
        );
        persistPreContractOpenDesign({
            includedModuleIds,
            includedRoofTagIds,
            ...(modulesLayout ? { modulesLayout } : {}),
            ...(ghostedTreeIds.length ? { ghostedTreeIds } : {}),
        });
        handleLaunch('openDesign', nextForm);
    };

    const handleFinalizeFromAi = async (option: PreContractAiDesignOption) => {
        markPreContractAutoFinalize();
        await handleOpenOneDrawFromAi(option);
    };

    const handleSaveOneButtonFromSelected = async () => {
        persistIntakeForm(site.salesForceId, form);
        return savePreContractOneButtonSelection(site.salesForceId, form);
    };

    const selectedAiPricingOverride = useMemo((): PreContractPpwMetricsOverride | null => {
        if (boardStep < 3) return null;
        const option = resolveSelectedPreContractAiOption();
        if (!option) return null;
        return {
            systemKw: option.systemKw > 0 ? option.systemKw : null,
            annualProductionKwh: option.annualKwh > 0 ? option.annualKwh : null,
            multiplier:
                option.multiplier != null && option.multiplier > 0 ? option.multiplier : null,
        };
    }, [
        boardStep,
        form.roofModuleLayouts,
        form.roofSelections,
        form.systemUsageKwh,
        form.siteTrees,
    ]);

    const utilityPopover =
        utilityPopoverOpen
            ? createPortal(
                  <div
                      ref={utilityPopoverRef}
                      className={`pci-utility-popover pci-utility-popover--portal${
                          utilityPopoverPos?.placement === 'above' ? ' pci-utility-popover--above' : ''
                      }`}
                      role="dialog"
                      aria-label="Utility details"
                      style={{
                          top: utilityPopoverPos?.top ?? -9999,
                          left: utilityPopoverPos?.left ?? -9999,
                          visibility: utilityPopoverPos ? 'visible' : 'hidden',
                      }}
                  >
                      <UtilityFormBody
                          form={form}
                          patchForm={patchForm}
                          utilityFromOpportunity={utilityFromOpportunity}
                      />
                  </div>,
                  document.body,
              )
            : null;

    const designToolPopover =
        designToolPopoverOpen
            ? createPortal(
                  <div
                      ref={designToolPopoverRef}
                      className={`pci-utility-popover pci-utility-popover--portal pci-design-tool-popover${
                          designToolPopoverPos?.placement === 'above' ? ' pci-utility-popover--above' : ''
                      }`}
                      role="dialog"
                      aria-label="Design tool"
                      style={{
                          top: designToolPopoverPos?.top ?? -9999,
                          left: designToolPopoverPos?.left ?? -9999,
                          visibility: designToolPopoverPos ? 'visible' : 'hidden',
                      }}
                  >
                      <DesignToolFormBody
                          form={form}
                          patchForm={patchForm}
                          onSelect={selectDesignTool}
                      />
                  </div>,
                  document.body,
              )
            : null;

    const moduleViewportSlot = moduleViewportActive ? (
        <section className="pci-module-step__card pci-module-step__card--viewport">
            {designViewerSlotMode === 'popup' ? (
                <aside
                    className="pci-module-step__viewport pci-module-step__viewport--delegated pci-design-viewer-slot pci-design-viewer-slot--module"
                    aria-label="Site design viewport"
                >
                    <span className="pci-module-step__viewport-hint">3D view open</span>
                </aside>
            ) : (
                <aside
                    ref={designViewerSlotRef}
                    className="pci-module-step__viewport pci-design-viewer-slot pci-design-viewer-slot--module"
                    aria-label="Site design viewport"
                />
            )}
        </section>
    ) : null;

    return (
        <div
            className={`pci-page pci-page--board-v2 pci-page--unified${
                pricingPanelOpen ? ' pci-page--board-v2-pricing' : ''
            }`}
        >
            {utilityPopover}
            {designToolPopover}
            <div className="pci-board-v2-shell pci-unified-shell">
            <main className="pci-board-v2-main pci-unified-main">
                <div
                    className={`pci-unified-workbench${
                        showPricingPreview ? '' : ' pci-unified-workbench--single-column'
                    }`}
                >
                <div
                    id="pci-unified-stack"
                    className={`pci-unified-stack${
                        showSelectedSection ? '' : ' pci-unified-stack--compact'
                    }`}
                >
                <div className="pci-board-v2-card pci-unified-header">
                    <div className="pci-unified-header__inner">
                        <div className="pci-unified-header__row">
                            <button
                                type="button"
                                className="pci-board-v2-back pci-unified-header__back"
                                onClick={goBackFromBoard}
                                aria-label={backAriaLabel}
                            >
                                <ArrowLeft size={16} weight="bold" />
                            </button>
                            <div className="pci-unified-header__lead">
                                <h1 className="pci-unified-header__title">{site.customerName}</h1>
                                <span className="pci-unified-header__stage">{site.stage}</span>
                            </div>
                            <div className="pci-unified-header__address" title={installDisplay}>
                                <MapPin size={14} weight="fill" aria-hidden />
                                <span>{installDisplay || '—'}</span>
                            </div>
                        </div>

                        {designToolReady ? (
                            <div
                                className="pci-unified-header__toolbar"
                                role="toolbar"
                                aria-label="Setup sections"
                            >
                                <span className="pci-unified-header__ready">
                                    {`${designToolLabel} ready`}
                                </span>
                                <div className="pci-unified-header__nav">
                                    {isEnergyCommunity ? (
                                        <span
                                            className="pci-unified-nav__badge pci-unified-nav__badge--energy-community"
                                            title="This property likely qualifies as an energy community."
                                            role="status"
                                        >
                                            <Info size={13} weight="fill" aria-hidden />
                                            Energy Community
                                        </span>
                                    ) : null}
                                    <div ref={utilityWrapRef} className="pci-utility-popover-wrap">
                                        <button
                                            ref={utilityButtonRef}
                                            type="button"
                                            className={`pci-unified-nav__btn${
                                                utilityPopoverOpen ? ' pci-unified-nav__btn--active' : ''
                                            }${
                                                utilityValidation.status === 'complete'
                                                    ? ' pci-unified-nav__btn--complete'
                                                    : ' pci-unified-nav__btn--needs'
                                            }${
                                                highlightValidation && utilityValidation.status !== 'complete'
                                                    ? ' pci-unified-nav__btn--attention'
                                                    : ''
                                            }`}
                                            aria-expanded={utilityPopoverOpen}
                                            aria-haspopup="dialog"
                                            onClick={toggleUtilityPopover}
                                        >
                                            Utility
                                        </button>
                                    </div>
                                    {showAiSection ? (
                                        <button
                                            type="button"
                                            className={`pci-unified-nav__btn pci-unified-nav__btn--ai${
                                                aiDesignsPopupOpen ? ' pci-unified-nav__btn--active' : ''
                                            }`}
                                            aria-expanded={aiDesignsPopupOpen}
                                            aria-haspopup="dialog"
                                            onClick={openAiDesignsPopup}
                                        >
                                            <Sparkle size={13} weight="fill" aria-hidden />
                                            AI Designs
                                        </button>
                                    ) : null}
                                    {showAddTitleButton ? (
                                        <button
                                            type="button"
                                            className={`pci-unified-nav__btn${
                                                titleValidation.status === 'complete'
                                                    ? ' pci-unified-nav__btn--complete'
                                                    : ' pci-unified-nav__btn--needs'
                                            }${
                                                highlightValidation && titleValidation.status !== 'complete'
                                                    ? ' pci-unified-nav__btn--attention'
                                                    : ''
                                            }`}
                                            onClick={() => void openAddTitleContactsModal()}
                                        >
                                            <IdentificationBadge size={13} weight="bold" aria-hidden />
                                            Add Title
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className={`pci-unified-nav__btn${
                                                activePanel === 'title' ? ' pci-unified-nav__btn--active' : ''
                                            }${
                                                titleValidation.status === 'complete'
                                                    ? ' pci-unified-nav__btn--complete'
                                                    : ' pci-unified-nav__btn--needs'
                                            }${
                                                highlightValidation && titleValidation.status !== 'complete'
                                                    ? ' pci-unified-nav__btn--attention'
                                                    : ''
                                            }`}
                                            onClick={() => openPanel('title')}
                                        >
                                            Title
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <p className="pci-unified-header__unavailable" role="status">
                                Aurora design unavailable — choose a design tool to continue.
                            </p>
                        )}
                    </div>

                    {!designToolReady ? (
                    <div id="pci-board-v2-card-body" className="pci-unified-header__setup">
                        <div className="pci-board-v2-section pci-board-v2-section--unavailable">
                            <div className="pci-design-unavailable" role="status">
                                <WarningCircle size={22} weight="fill" />
                                <div className="pci-design-unavailable__copy">
                                    <strong>
                                        {form.auroraAvailable
                                            ? 'Select a design tool'
                                            : 'Aurora design is not available'}
                                    </strong>
                                    <p>
                                        {form.auroraAvailable
                                            ? 'Choose Aurora or Hover to continue with products, setup, and details.'
                                            : 'Products, setup, and details are hidden until a design tool is selected. Choose Hover if it is available for this opportunity.'}
                                    </p>
                                </div>
                                <button
                                    ref={designToolButtonRef}
                                    type="button"
                                    className="pci-btn pci-btn--primary pci-btn--sm"
                                    aria-expanded={designToolPopoverOpen}
                                    aria-haspopup="dialog"
                                    onClick={toggleDesignToolPopover}
                                >
                                    Choose design tool
                                </button>
                            </div>
                        </div>
                    </div>
                    ) : null}
                </div>

                {designToolReady ? (
                    <section className="pci-unified-deal" aria-label="Products">
                        <header className="pci-unified-deal__head">
                            <h2 className="pci-unified-deal__title">Products</h2>
                            <p className="pci-unified-deal__subtitle">
                                Tap a card to open its design
                            </p>
                        </header>
                        <div
                            className={`pci-product-cards${
                                selectedProducts.length === 0 ? ' pci-product-cards--empty' : ''
                            }`}
                            role="list"
                            aria-label="Selected products"
                        >
                            {selectedProducts.map((product) => {
                                const meta = PRODUCT_META[product];
                                return (
                                    <PreContractProductDealCard
                                        key={product}
                                        product={product}
                                        form={form}
                                        icon={meta.icon}
                                        accent={meta.accent}
                                        label={productPillLabel(product)}
                                        metricsOverride={selectedAiPricingOverride}
                                        summaryItems={summaryItems}
                                        preferEnfinPartner={isEnergyCommunity}
                                        onOpenDesign={() => openProductDesign(product)}
                                        onRemove={() => removeProduct(product)}
                                        onOpenPricingDetails={
                                            product === 'Solar' ? handleOpenPricingPanel : undefined
                                        }
                                        onGenerateQuote={
                                            product === 'Solar'
                                                ? () => void handleGenerateQuoteFromPreview()
                                                : undefined
                                        }
                                        generateQuoteDisabled={
                                            product === 'Solar'
                                                ? Boolean(generateQuoteDisabledReason) ||
                                                  generateQuoteLoading
                                                : undefined
                                        }
                                        generateQuoteDisabledReason={
                                            product === 'Solar'
                                                ? generateQuoteLoading
                                                    ? 'Loading quote details…'
                                                    : generateQuoteDisabledReason
                                                : undefined
                                        }
                                        designRevision={
                                            product === 'Solar' ? form.solarDesignRevision : null
                                        }
                                        designModified={
                                            product === 'Solar' ? solarDesignModified : false
                                        }
                                        appliedDesignLabel={
                                            product === 'Solar' ? form.appliedAiDesignTitle : null
                                        }
                                    />
                                );
                            })}
                            {availableProductsToAdd.length > 0 ? (
                                <button
                                    type="button"
                                    role="listitem"
                                    className="pci-product-card-tile pci-product-card-tile--add"
                                    aria-label="Add product"
                                    title="Add product"
                                    onClick={() => setProductPickerOpen(true)}
                                >
                                    <Plus size={32} weight="bold" aria-hidden />
                                    <span className="pci-product-card-tile__add-label">Add product</span>
                                </button>
                            ) : null}
                        </div>
                    </section>
                ) : null}

                {(siteViewportError || siteViewportLoadingMessage) && showSolarLayoutStatus ? (
                    <p
                        className={`pci-board-fab__status${
                            siteViewportError ? ' pci-board-fab__status--error' : ''
                        }`}
                        role={siteViewportError ? 'alert' : 'status'}
                    >
                        {siteViewportError ? (
                            <WarningCircle size={14} weight="fill" aria-hidden />
                        ) : (
                            <CircleNotch
                                className="pci-board-v2-next__loading-icon"
                                size={14}
                                weight="bold"
                                aria-hidden
                            />
                        )}
                        <span>{siteViewportError || siteViewportLoadingMessage}</span>
                    </p>
                ) : null}

                {showSelectedSection ? (
                    <PreContractSelectedDesignStep
                        form={form}
                        patchForm={patchForm}
                        embedded
                        onBack={goBackFromBoard}
                        onOpenPricing={handleOpenPricingPanel}
                        onOpenOneDraw={handleOpenOneDrawFromAi}
                        onFinalize={handleFinalizeFromAi}
                        onSaveOneButton={handleSaveOneButtonFromSelected}
                        actionsDisabled={!allComplete}
                    />
                ) : null}
                </div>
                {showPricingPreview ? (
                    <PreContractSelectedPricingPreview
                        form={form}
                        metricsOverride={selectedAiPricingOverride}
                        summaryItems={summaryItems}
                        preferEnfinPartner={isEnergyCommunity}
                        moduleStepLayout
                        onOpenDetails={handleOpenPricingPanel}
                        onGenerateQuote={() => void handleGenerateQuoteFromPreview()}
                        generateQuoteDisabled={
                            Boolean(generateQuoteDisabledReason) || generateQuoteLoading
                        }
                        generateQuoteDisabledReason={
                            generateQuoteLoading
                                ? 'Loading quote details…'
                                : generateQuoteDisabledReason
                        }
                    />
                ) : null}
                </div>
            </main>
            {pricingPanelOpen
                ? createPortal(
                      <>
                          <button
                              type="button"
                              className="pci-pricing-sidepanel-backdrop"
                              onClick={() => setPricingPanelOpen(false)}
                              aria-label="Close pricing panel"
                          />
                          <aside className="pci-pricing-sidepanel" aria-label="Finance partner pricing">
                              <PreContractLivePricingRail
                                  form={form}
                                  metricsOverride={selectedAiPricingOverride}
                                  preferEnfinPartner={isEnergyCommunity}
                                  onClose={() => setPricingPanelOpen(false)}
                              />
                          </aside>
                      </>,
                      getPciViewportPortalRoot(),
                  )
                : null}
            </div>

            {designToolReady ? (
                <nav
                    className={`pci-unified-mobile-nav${
                        showAiSection ? '' : ' pci-unified-mobile-nav--three'
                    }`}
                    aria-label="Quick navigation"
                >
                    <button
                        type="button"
                        className="pci-unified-mobile-nav__btn"
                        onClick={openSolarLayoutPopup}
                    >
                        <Sun size={18} weight="duotone" aria-hidden />
                        <span>Layout</span>
                    </button>
                    {showAiSection ? (
                        <button
                            type="button"
                            className={`pci-unified-mobile-nav__btn${
                                aiDesignsPopupOpen ? ' pci-unified-mobile-nav__btn--active' : ''
                            }`}
                            onClick={openAiDesignsPopup}
                        >
                            <Sparkle size={18} weight="fill" aria-hidden />
                            <span>AI</span>
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="pci-unified-mobile-nav__btn"
                        onClick={handleOpenPricingPanel}
                    >
                        <CurrencyDollar size={18} weight="bold" aria-hidden />
                        <span>Pricing</span>
                    </button>
                    <button
                        type="button"
                        className="pci-unified-mobile-nav__btn"
                        onClick={() => openPanel('design')}
                    >
                        <Wrench size={18} weight="duotone" aria-hidden />
                        <span>Setup</span>
                    </button>
                </nav>
            ) : null}

            {activePanel ? (
                <>
                    <button
                        type="button"
                        className="pci-drawer-backdrop"
                        onClick={closePanel}
                        aria-label="Close panel"
                    />
                    <aside
                        className={`pci-drawer${roofDesignDrawerOpen ? ' pci-drawer--roof-design' : ''}`}
                        aria-label={designDrawerTitle || undefined}
                    >
                        <div className="pci-drawer__handle" aria-hidden />
                        <header className="pci-drawer__head">
                            <h2>{designDrawerTitle}</h2>
                            {activePanelValidation ? (
                                <ValidationBadge validation={activePanelValidation} compact />
                            ) : null}
                            <button
                                type="button"
                                className="pci-drawer__close"
                                onClick={closePanel}
                                aria-label="Close"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </header>
                        {activePanelValidation && activePanelValidation.status !== 'complete' ? (
                            <ValidationPanel validation={activePanelValidation} />
                        ) : null}
                        <div className="pci-drawer__body">
                            <StepBody
                                stepId={PANEL_STEPS[activePanel]}
                                form={form}
                                patchForm={patchForm}
                                site={site}
                                installDisplay={installDisplay}
                                designIssues={designIssues}
                                utilityFromOpportunity={utilityFromOpportunity}
                                titleLoading={titleLoading}
                                titleInfo={titleInfo}
                                titleInfoAvailable={titleInfoAvailable}
                                titleContactName={titleContactName}
                                onOpenAddTitle={() => void openAddTitleContactsModal()}
                                designPanelOpen={activePanel === 'design'}
                                onDesignPanelClose={closePanel}
                                onDesignDrawerFooterChange={setDesignDrawerFooter}
                                theme={theme}
                                designProductFocus={designProductFocus}
                            />
                        </div>
                        <footer
                            className={`pci-drawer__footer${
                                designDrawerFooter ? ' pci-drawer__footer--embedded' : ''
                            }`}
                        >
                            {designDrawerFooter ?? (
                                <button
                                    type="button"
                                    className="pci-drawer__footer-close"
                                    onClick={closePanel}
                                >
                                    Close
                                </button>
                            )}
                        </footer>
                    </aside>
                </>
            ) : null}

            {productPickerOpen
                ? createPortal(
                      <div
                          className="pci-product-picker-popup"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="pci-product-picker-title"
                      >
                          <button
                              type="button"
                              className="pci-product-picker-popup__backdrop"
                              onClick={() => setProductPickerOpen(false)}
                              aria-label="Close product picker"
                          />
                          <div className="pci-product-picker-popup__dialog">
                              <header className="pci-product-picker-popup__head">
                                  <h2 id="pci-product-picker-title">Add product</h2>
                                  <button
                                      type="button"
                                      className="pci-product-picker-popup__close"
                                      onClick={() => setProductPickerOpen(false)}
                                      aria-label="Close"
                                  >
                                      <X size={18} weight="bold" />
                                  </button>
                              </header>
                              <div className="pci-product-picker-popup__body">
                                  {availableProductsToAdd.length > 0 ? (
                                      <div className="pci-product-picker-popup__grid">
                                          {availableProductsToAdd.map((product) => {
                                              const meta = PRODUCT_META[product];
                                              return (
                                                  <button
                                                      key={product}
                                                      type="button"
                                                      className={`pci-product-picker-card pci-product-picker-card--${meta.accent}`}
                                                      onClick={() => addProductAndOpenDesign(product)}
                                                  >
                                                      <span className="pci-product-picker-card__icon">
                                                          {meta.icon}
                                                      </span>
                                                      <span className="pci-product-picker-card__name">
                                                          {product}
                                                      </span>
                                                      <span className="pci-product-picker-card__desc">
                                                          {meta.description}
                                                      </span>
                                                  </button>
                                              );
                                          })}
                                      </div>
                                  ) : (
                                      <p className="pci-product-picker-popup__empty" role="status">
                                          All available products have been added.
                                      </p>
                                  )}
                              </div>
                          </div>
                      </div>,
                      getPciViewportPortalRoot(),
                  )
                : null}

            {solarLayoutPopupActive
                ? createPortal(
                      <div
                          className="pci-solar-layout-popup"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="pci-solar-layout-popup-title"
                      >
                          <button
                              type="button"
                              className="pci-solar-layout-popup__backdrop"
                              onClick={closeSolarLayoutPopup}
                              aria-label="Close solar layout"
                          />
                          <div className="pci-solar-layout-popup__dialog">
                              <header className="pci-solar-layout-popup__head">
                                  <h2 id="pci-solar-layout-popup-title">Solar layout</h2>
                                  <button
                                      type="button"
                                      className="pci-solar-layout-popup__close"
                                      onClick={closeSolarLayoutPopup}
                                      aria-label="Close"
                                  >
                                      <X size={18} weight="bold" />
                                  </button>
                              </header>
                              <div className="pci-solar-layout-popup__body">
                                  <div className="pci-unified-layout-row">
                                      <div
                                          id="pci-section-viewport"
                                          className="pci-unified-section pci-unified-layout-row__viewport"
                                      >
                                          {moduleViewportSlot}
                                      </div>
                                      <PreContractModuleStep
                                          form={form}
                                          patchForm={patchForm}
                                          summaryItems={summaryItems}
                                          onBack={closeSolarLayoutPopup}
                                          onRunAiDesign={handleRunAiDesign}
                                          onOpenPricing={handleOpenPricingPanel}
                                          runAiDisabled={!canRunAi}
                                          actionsDisabled={!allComplete}
                                          hasPreviousAiDesigns={hasPreviousAiDesigns}
                                          embedded
                                          renderViewport={false}
                                          onTreeCuttingActiveChange={setTreeCuttingPreviewActive}
                                      />
                                  </div>
                              </div>
                          </div>
                      </div>,
                      getPciViewportPortalRoot(),
                  )
                : null}

            {aiDesignsPopupActive
                ? createPortal(
                      <div
                          className="pci-ai-designs-popup"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="pci-ai-designs-popup-title"
                      >
                          <button
                              type="button"
                              className="pci-ai-designs-popup__backdrop"
                              onClick={closeAiDesignsPopup}
                              aria-label="Close AI designs"
                          />
                          <div className="pci-ai-designs-popup__dialog">
                              <header className="pci-ai-designs-popup__head">
                                  <Sparkle size={18} weight="fill" aria-hidden />
                                  <h2 id="pci-ai-designs-popup-title">AI Designs</h2>
                                  <button
                                      type="button"
                                      className="pci-ai-designs-popup__close"
                                      onClick={closeAiDesignsPopup}
                                      aria-label="Close"
                                  >
                                      <X size={18} weight="bold" />
                                  </button>
                              </header>
                              <div className="pci-ai-designs-popup__body">
                                  <PreContractAiDesignsStep
                                      key={aiDesignRunId}
                                      form={form}
                                      patchForm={patchForm}
                                      embedded
                                      onBack={goBackFromBoard}
                                      onApply={handleApplyAiDesign}
                                      onNext={handleAiDesignsContinue}
                                      onOpenViewport={openViewportPopup}
                                      onPreviewAiOption={(option) => {
                                          if (viewportPopupOpen) applyAiOptionToEmbed(option);
                                      }}
                                      viewportAvailable={designViewerKept}
                                      actionsDisabled={!allComplete}
                                  />
                              </div>
                          </div>
                      </div>,
                      getPciViewportPortalRoot(),
                  )
                : null}

            {viewportPopupActive
                ? createPortal(
                      <div
                          className="pci-viewport-popup"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="pci-viewport-popup-title"
                      >
                          <button
                              type="button"
                              className="pci-viewport-popup__backdrop"
                              onClick={closeViewportPopup}
                              aria-label="Close 3D view"
                          />
                          <div className="pci-viewport-popup__dialog">
                              <header className="pci-viewport-popup__head">
                                  <h2 id="pci-viewport-popup-title">3D view</h2>
                                  <button
                                      type="button"
                                      className="pci-viewport-popup__close"
                                      onClick={closeViewportPopup}
                                      aria-label="Close"
                                  >
                                      ×
                                  </button>
                              </header>
                              <div
                                  ref={
                                      designViewerSlotMode === 'popup'
                                          ? designViewerSlotRef
                                          : undefined
                                  }
                                  className="pci-viewport-popup__slot pci-design-viewer-slot"
                              />
                          </div>
                      </div>,
                      getPciViewportPortalRoot(),
                  )
                : null}
            {designViewerKept
                ? createPortal(
                      <div
                          ref={designViewerHostRef}
                          className={`pci-onedraw-host${
                              designViewerDocked
                                  ? ` pci-onedraw-host--docked${
                                        dockHostInSlot ? ' pci-onedraw-host--in-slot' : ''
                                    }${
                                        viewportPopupActive
                                            ? ' pci-onedraw-host--docked-popup'
                                            : dockHostElevated
                                              ? ' pci-onedraw-host--docked-elevated'
                                              : ''
                                    }`
                                  : ' pci-onedraw-host--parked'
                          }`}
                          style={
                              !designViewerDocked && lastDesignViewerDockRectRef.current
                                  ? {
                                        // Match last dock size while parked so reopen doesn't cold-resize WebGL.
                                        width: lastDesignViewerDockRectRef.current.width,
                                        height: lastDesignViewerDockRectRef.current.height,
                                    }
                                  : undefined
                          }
                          aria-hidden={!designViewerDocked}
                      >
                          <PreContractOneDrawEmbed salesForceId={site.salesForceId} />
                      </div>,
                      dockHostInSlot && dockSlotEl
                          ? dockSlotEl
                          : getPciViewportPortalRoot(),
                  )
                : null}
            <AddTitleContactsModal
                open={addTitleContactsModalOpen}
                setOpen={setAddTitleContactsModalOpen}
                initialData={titleSearchData}
                onSuccess={handleTitleSyncSuccess}
            />
            {confirmQuoteCellKey
                ? createPortal(
                      <GenerateQuoteModal
                          confirmQuoteCellKey={confirmQuoteCellKey}
                          quoteForm={quoteForm}
                          priceQuote={priceQuoteDetails}
                          setConfirmQuoteCellKey={setConfirmQuoteCellKey}
                          setQuoteForm={setQuoteForm}
                          updateQuoteVersion={handleGenerateQuoteComplete}
                          rcomendedQuote
                      />,
                      getPciViewportPortalRoot(),
                  )
                : null}
        </div>
    );
};

function ValidationBadge({
    validation,
    compact = false,
}: {
    validation: StepValidation;
    compact?: boolean;
}) {
    const icon =
        validation.status === 'complete' ? (
            <CheckCircle size={compact ? 14 : 14} weight="fill" />
        ) : validation.status === 'error' ? (
            <WarningCircle size={compact ? 14 : 14} weight="fill" />
        ) : (
            <Circle size={compact ? 14 : 14} weight="bold" />
        );

    return (
        <span
            className={`pci-validation-badge pci-validation-badge--${validation.status}${
                compact ? ' pci-validation-badge--compact' : ''
            }`}
        >
            {icon}
            {validation.label}
        </span>
    );
}

function ValidationPanel({ validation }: { validation: StepValidation }) {
    const title =
        validation.status === 'error' ? 'Resolve before launch' : 'Complete this section';

    return (
        <div
            className={`pci-validation-panel pci-validation-panel--${validation.status}`}
            role="alert"
        >
            <WarningCircle size={18} weight="fill" />
            <div>
                <strong>{title}</strong>
                <ul className="pci-validation-panel__list">
                    {validation.issues.map((issue) => (
                        <li key={issue}>{issue}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

type StepBodyProps = {
    stepId: WizardStepId;
    form: PreContractIntakeForm;
    patchForm: (patch: Partial<PreContractIntakeForm>) => void;
    site: PreContractSiteContext;
    installDisplay: string;
    designIssues: string[];
    utilityFromOpportunity: boolean;
    titleLoading: boolean;
    titleInfo: Record<string, unknown> | null;
    titleInfoAvailable: boolean;
    titleContactName: string;
    onOpenAddTitle: () => void;
    designPanelOpen?: boolean;
    onDesignPanelClose?: () => void;
    onDesignDrawerFooterChange?: (footer: ReactNode | null) => void;
    theme?: string;
    designProductFocus?: PreContractProduct | null;
};

function UtilityFormBody({
    form,
    patchForm,
    utilityFromOpportunity,
}: {
    form: PreContractIntakeForm;
    patchForm: (patch: Partial<PreContractIntakeForm>) => void;
    utilityFromOpportunity: boolean;
}) {
    const utilityOptions = utilityCompanySelectOptions(form.utilityCompany);
    return (
        <div className="pci-utility-popover__form">
            {utilityFromOpportunity ? (
                <p className="pci-opp-prefill pci-opp-prefill--compact" role="status">
                    <CheckCircle size={14} weight="fill" />
                    From opportunity Utility Details
                </p>
            ) : null}
            <label className="pci-field pci-field--full">
                <span>Utility company</span>
                <select
                    value={form.utilityCompany}
                    onChange={(e) => patchForm({ utilityCompany: e.target.value })}
                >
                    <option value="">Select utility provider</option>
                    {utilityOptions.map((u) => (
                        <option key={u} value={u}>
                            {u}
                        </option>
                    ))}
                </select>
            </label>
            <label className="pci-field">
                <span>Annual usage (kWh)</span>
                <input
                    type="number"
                    value={form.annualUsageKwh}
                    onChange={(e) => patchForm({ annualUsageKwh: e.target.value })}
                />
            </label>
            <label className="pci-field">
                <span>All-in rate ($/kWh)</span>
                <input
                    type="number"
                    step="0.001"
                    value={form.utilityRate}
                    onChange={(e) => patchForm({ utilityRate: e.target.value })}
                />
            </label>
        </div>
    );
}

function DesignToolFormBody({
    form,
    patchForm,
    onSelect,
}: {
    form: PreContractIntakeForm;
    patchForm: (patch: Partial<PreContractIntakeForm>) => void;
    onSelect: (tool: PreContractDesignTool) => void;
}) {
    const tools: {
        id: PreContractDesignTool;
        label: string;
        available: boolean;
        availableKey: 'auroraAvailable' | 'hoverAvailable';
    }[] = [
        {
            id: 'aurora',
            label: 'Aurora',
            available: form.auroraAvailable,
            availableKey: 'auroraAvailable',
        },
        {
            id: 'hover',
            label: 'Hover',
            available: form.hoverAvailable,
            availableKey: 'hoverAvailable',
        },
    ];

    return (
        <div className="pci-utility-popover__form pci-design-tool-form">
            <p className="pci-section-lead pci-design-tool-form__lead">
                Choose an available design tool for this opportunity.
            </p>
            <div className="pci-design-tool-form__list">
                {tools.map((tool) => {
                    const selected = form.preferredDesignTool === tool.id && tool.available;
                    return (
                        <div key={tool.id} className="pci-design-tool-option">
                            <button
                                type="button"
                                className={`pci-design-tool-option__select${
                                    selected ? ' pci-design-tool-option__select--on' : ''
                                }${tool.available ? '' : ' pci-design-tool-option__select--disabled'}`}
                                disabled={!tool.available}
                                onClick={() => onSelect(tool.id)}
                            >
                                <strong>{tool.label}</strong>
                                <span>{tool.available ? (selected ? 'Selected' : 'Available') : 'Not available'}</span>
                            </button>
                            <button
                                type="button"
                                className={`pci-design-tool-option__toggle${
                                    tool.available ? ' pci-design-tool-option__toggle--on' : ''
                                }`}
                                onClick={() => {
                                    const nextAvailable = !tool.available;
                                    const patch: Partial<PreContractIntakeForm> = {
                                        [tool.availableKey]: nextAvailable,
                                    };
                                    if (!nextAvailable && form.preferredDesignTool === tool.id) {
                                        patch.preferredDesignTool = null;
                                    }
                                    if (nextAvailable && !form.preferredDesignTool) {
                                        patch.preferredDesignTool = tool.id;
                                    }
                                    patchForm(patch);
                                }}
                            >
                                {tool.available ? 'Mark unavailable' : 'Mark available'}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function StepBody({
    stepId,
    form,
    patchForm,
    site,
    installDisplay,
    designIssues,
    utilityFromOpportunity,
    titleLoading,
    titleInfo,
    titleInfoAvailable,
    titleContactName,
    onOpenAddTitle,
    designPanelOpen = false,
    onDesignPanelClose,
    onDesignDrawerFooterChange,
    theme = 'light',
    designProductFocus = null,
}: StepBodyProps) {
    switch (stepId) {
        case 'products':
            return (
                <>
                    <p className="pci-section-lead">Select every product included in this deal.</p>
                    <div className="pci-product-grid">
                        {PRE_CONTRACT_PRODUCTS.map((product) => {
                            const meta = PRODUCT_META[product];
                            const selected = form.products[product];
                            const disabled = PRE_CONTRACT_DISABLED_PRODUCTS.has(product);
                            return (
                                <button
                                    key={product}
                                    type="button"
                                    className={`pci-product-card pci-product-card--${meta.accent}${
                                        selected ? ' pci-product-card--selected' : ''
                                    }${disabled ? ' pci-product-card--disabled' : ''}`}
                                    aria-disabled={disabled}
                                    disabled={disabled}
                                    title={disabled ? 'Coming soon' : undefined}
                                    onClick={() => {
                                        if (disabled) return;
                                        patchForm({
                                            products: {
                                                ...form.products,
                                                [product]: !selected,
                                            },
                                        });
                                    }}
                                >
                                    <span className="pci-product-card__icon">{meta.icon}</span>
                                    <span className="pci-product-card__name">{product}</span>
                                    <span className="pci-product-card__desc">{meta.description}</span>
                                    {selected ? (
                                        <span className="pci-product-card__check">
                                            <Check size={14} weight="bold" />
                                        </span>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                </>
            );

        case 'site':
            return (
                <>
                    <p className="pci-section-lead">Confirm the job site address from the sites table.</p>
                    <div className="pci-site-layout">
                        <div className="pci-field-stack">
                            <label className="pci-field pci-field--full">
                                <span>Street address</span>
                                <input
                                    value={form.installStreet}
                                    onChange={(e) => patchForm({ installStreet: e.target.value })}
                                />
                            </label>
                            <div className="pci-field-row">
                                <label className="pci-field">
                                    <span>City</span>
                                    <input
                                        value={form.installCity}
                                        onChange={(e) => patchForm({ installCity: e.target.value })}
                                    />
                                </label>
                                <label className="pci-field">
                                    <span>State</span>
                                    <select
                                        value={form.installState}
                                        onChange={(e) => patchForm({ installState: e.target.value })}
                                    >
                                        <option value="">Select</option>
                                        {US_STATE_OPTIONS.map((st) => (
                                            <option key={st.value} value={st.value}>
                                                {st.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="pci-field pci-field--zip">
                                    <span>ZIP</span>
                                    <input
                                        value={form.installZip}
                                        onChange={(e) => patchForm({ installZip: e.target.value })}
                                    />
                                </label>
                            </div>
                        </div>
                        <div className="pci-map-preview">
                            <MapPin size={28} weight="duotone" />
                            <span>{installDisplay}</span>
                        </div>
                    </div>
                </>
            );

        case 'property': {
            if (titleLoading) {
                return (
                    <div className="pci-title-empty" role="status">
                        <CircleNotch className="pci-board-v2-next__loading-icon" size={18} weight="bold" />
                        <p>Loading title records…</p>
                    </div>
                );
            }

            if (!titleInfoAvailable) {
                return (
                    <div className="pci-title-empty" role="status">
                        <WarningCircle size={22} weight="fill" />
                        <div className="pci-title-empty__copy">
                            <strong>Title info is not available</strong>
                            <p>
                                Title records are not available. Add title contacts, or run Black Knight
                                from that window if no contacts are found.
                            </p>
                        </div>
                        <div className="pci-title-empty__actions">
                            <button
                                type="button"
                                className="pci-btn pci-btn--primary pci-btn--sm"
                                onClick={onOpenAddTitle}
                            >
                                <IdentificationBadge size={16} weight="bold" aria-hidden />
                                Add Title
                            </button>
                        </div>
                    </div>
                );
            }

            const fields = extractTitleFields(titleInfo);
            const owner = fields.owner || titleContactName || site.customerName;
            const ownerLooksMatched =
                Boolean(fields.owner || titleContactName) &&
                site.customerName
                    .toLowerCase()
                    .split(/\s+/)
                    .filter((part) => part.length > 2)
                    .some((part) => owner.toLowerCase().includes(part));
            return (
                <>
                    <p className="pci-section-lead">Verify ownership matches your primary customer.</p>
                    <div className="pci-verify-card">
                        <div className="pci-verify-card__status">
                            <Sparkle size={18} weight="fill" />
                            {fields.owner || fields.apn
                                ? 'Pulled from title records'
                                : 'Synced from opportunity contacts'}
                        </div>
                        <dl className="pci-verify-list">
                            <div>
                                <dt>Owner on title</dt>
                                <dd>{owner}</dd>
                            </div>
                            <div>
                                <dt>Property type</dt>
                                <dd>{fields.propertyType || '—'}</dd>
                            </div>
                            <div>
                                <dt>APN</dt>
                                <dd>{fields.apn || '—'}</dd>
                            </div>
                            <div>
                                <dt>Match status</dt>
                                <dd className={ownerLooksMatched ? 'pci-verify-match' : undefined}>
                                    {ownerLooksMatched ? 'Customer name aligns' : 'Review owner against the primary customer'}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </>
            );
        }

        case 'utility':
            return (
                <UtilityFormBody
                    form={form}
                    patchForm={patchForm}
                    utilityFromOpportunity={utilityFromOpportunity}
                />
            );

        case 'design': {
            const showRoofProductDesign =
                form.products.Roof && designProductFocus !== 'Battery';
            const showSolarSlopeOnly =
                form.products.Solar &&
                !form.products.Roof &&
                designProductFocus !== 'Battery';
            const showBattery =
                form.products.Battery && designProductFocus !== 'Roof';
            return (
                <div
                    className={`pci-design-step${
                        showRoofProductDesign ? ' pci-design-step--roof-modal' : ''
                    }`}
                >
                    {designIssues.length > 0 && !showRoofProductDesign ? (
                        <div className="pci-alert" role="alert">
                            <WarningCircle size={18} weight="fill" />
                            {designIssues.join(' · ')}
                        </div>
                    ) : null}

                    {showRoofProductDesign ? (
                        <div
                            className={`pci-roof-design-host obd-product-designs-modal-root obd-product-designs-modal-root--roof obd-theme-${
                                theme === 'dark' ? 'dark' : 'light'
                            }`}
                        >
                            <RoofDetailsModal
                                embedded
                                embeddedActive={designPanelOpen}
                                open={designPanelOpen}
                                onClose={onDesignPanelClose ?? (() => undefined)}
                                onEmbeddedFooterChange={onDesignDrawerFooterChange}
                            />
                        </div>
                    ) : null}

                    {showSolarSlopeOnly ? (
                        <PreContractRoofSlopeSection
                            salesForceId={site.salesForceId}
                            form={form}
                            patchForm={patchForm}
                        />
                    ) : null}

                    {showBattery ? (
                        <section className="pci-design-block pci-design-block--battery">
                            <PreContractBatterySection form={form} patchForm={patchForm} />
                        </section>
                    ) : null}
                </div>
            );
        }

        default:
            return null;
    }
}

export default PreContractIntake;
